<template>
  <div>
    <!-- <h1 class="text-h4 mb-4"><v-icon class="mr-2">mdi-form-select</v-icon>Sales</h1> -->

    <!-- Sales Form Card -->
    <v-card class="mb-4 pa-4">
      <div class="text-center text-h6 mb-4">
        Enter Sales data for
        <span class="text-warning font-weight-bold">{{ formatDay(form.sales_date) }}</span>
      </div>
      <v-form @submit.prevent="submitSale">
        <!-- City Dropdown -->
        <v-row>
          <v-col cols="6">
            <v-select
              v-model="form.city"
              :items="options.cities"
              item-title="name"
              item-value="name"
              label="City"
              required
              density="comfortable"
              variant="outlined"
              :rules="[(v) => !!v || 'City is required. Select a value']"
            ></v-select>
          </v-col>

          <!-- Location Field -->
          <v-col cols="6">
            <v-select
              v-model="form.location"
              :items="filteredLocations"
              item-title="name"
              item-value="name"
              label="Location"
              required
              density="comfortable"
              variant="outlined"
              :rules="[(v) => !!v || 'Location is required. Select a value']"
              :disabled="!form.city"
              :key="form.city"
              persistent-hint
              :hint="locationHint"
            >
              <template v-slot:message="{ message }">
                <div v-if="!form.city" class="text-caption text-warning">Select a city first</div>
                <div v-else-if="filteredLocations.length === 0" class="text-caption text-warning">
                  No locations available for {{ form.city }}
                </div>
                <div v-else>{{ message }}</div>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <v-select
              v-model="form.brand"
              :items="options.brands"
              item-title="name"
              item-value="name"
              label="Brand"
              required
              density="comfortable"
              variant="outlined"
              :rules="[(v) => !!v || 'Brand is required. Select a value']"
            ></v-select>
          </v-col>

          <v-col cols="6">
            <v-select
              v-model="form.platform"
              :items="filteredPlatforms"
              item-title="name"
              item-value="name"
              label="Platform"
              required
              density="comfortable"
              variant="outlined"
              :rules="[(v) => !!v || 'Platform is required. Select a value']"
              persistent-hint
              :hint="platformHint"
            >
              <template v-slot:item="{ item, props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <div :style="{ background: getPlatformGradient(item.name), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <div :style="{ background: getPlatformGradient(item.name), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                  {{ item.name }}
                </div>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="form.amount"
              label="Amount"
              type="number"
              step="1"
              required
              prefix="AED"
              density="comfortable"
              variant="outlined"
              :rules="[
                (v) => {
                  if (v === null || v === undefined || v === '')
                    return 'Amount is required. Enter a value';
                  const num = parseFloat(v);
                  if (isNaN(num)) return 'Amount must be a valid number';
                  if (num < 0) return 'Amount cannot be negative';
                  return true;
                },
              ]"
              persistent-hint
              hint="Total sales of the day"
              @input="checkAmount"
            ></v-text-field>
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model="form.orders"
              label="Orders"
              type="number"
              step="1"
              required
              density="comfortable"
              variant="outlined"
              :rules="[
                (v) => {
                  if (v === null || v === undefined || v === '')
                    return 'Number of orders is required. Enter a value';
                  const num = parseFloat(v);
                  if (isNaN(num)) return 'Number of orders must be a valid number';
                  if (num < 0) return 'Number of orders cannot be negative';
                  return true;
                },
              ]"
              persistent-hint
              hint="Number of orders"
              @input="checkOrders"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <!-- Quick Date Range Presets -->
          <v-col cols="12">
            <v-text-field
              v-model="form.sales_date"
              type="date"
              required
              density="comfortable"
              variant="outlined"
              :rules="[(v) => !!v || 'Sales date is required. Enter or select a value']"
              class="font-weight-bold text-warning"
            >
              <template v-slot:label
                ><span class="text-white font-weight-regular"
                  >Sales Date (dd/mm/year)</span
                ></template
              >
            </v-text-field>
          </v-col>
        </v-row>

        <!-- High Amount Alert -->
        <v-alert
          v-if="showAmountAlert"
          type="warning"
          class="mb-4"
          :icon="false"
          variant="outlined"
        >
          <div class="d-flex align-center justify-space-between">
            <div>
              <strong>High Amount Alert</strong>
              <div class="text-caption">
                Amount ({{ formatAmount(form.amount) }} AED) exceeds 10,000 AED
              </div>
            </div>
            <v-btn size="small" color="warning" variant="text" @click="showAmountAlert = false">
              Acknowledge
            </v-btn>
          </div>
        </v-alert>

        <!-- High Orders Alert -->
        <v-alert v-if="showOrdersAlert" type="info" class="my-4" :icon="false" variant="outlined">
          <div class="d-flex align-center justify-space-between">
            <div>
              <strong>High Orders Alert</strong>
              <div class="text-caption">Orders ({{ form.orders }}) exceed 100 orders</div>
            </div>
            <v-btn size="small" color="info" variant="text" @click="showOrdersAlert = false">
              Acknowledge
            </v-btn>
          </div>
        </v-alert>

        <!-- Duplicate Warning -->
        <v-alert v-if="duplicateWarning" type="warning" class="my-4" :icon="false">
          <div class="d-flex align-center justify-space-between">
            <div>
              <strong>Duplicate Entry Found</strong>
              <div class="text-caption">
                A sale record already exists for this combination on {{ form.sales_date }}
              </div>
            </div>
            <v-btn size="small" color="primary" variant="flat" @click="loadExistingRecord">
              Load & Edit
            </v-btn>
          </div>
        </v-alert>

        <!-- Edit Mode Info -->
        <v-alert v-if="isEditMode" class="my-4" variant="outlined">
          <div>
            <strong>Editing Existing Record</strong>
            <div class="text-caption">You are updating record #{{ editingId }}</div>
            <div v-if="currentSaleReporter" class="text-caption">
              Original reporter: {{ currentSaleReporter }}
            </div>
          </div>
          <div class="mt-2">
            <v-btn
              v-if="isEditMode"
              @click="handleCancel"
              color="secondary"
              variant="outlined"
              size="small"
              class="mr-2"
            >
              {{ sourceComponent === "rawdata" ? "Close" : "Cancel Edit" }}
            </v-btn>
            <v-btn
              size="small"
              color="error"
              variant="flat"
              @click="deleteRecord"
              :disabled="!canDeleteRecord"
            >
              Delete Sale
            </v-btn>
          </div>
        </v-alert>

        <!---- submit button ----->
        <v-row>
          <v-col>
            <v-btn
              block
              type="submit"
              color="primary"
              variant="flat"
              :loading="loading"
              class="mt-0"
            >
              {{ isEditMode ? "Update sales" : "Submit sales" }}
            </v-btn>
          </v-col>
          <v-col class="text-right">
            <v-btn block variant="outlined" class="mt-0" @click="clearForm"> Clear Form </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- Recent Sales for Reference - Conditionally shown -->
    <div v-if="recentSales.length > 0 && !hideRecentSales" class="mt-6">
      <div class="text-subtitle-1 text-disabled mb-3">
        {{ isAdmin ? "Recent Sales" : "Your Recent Sales" }} - Click to edit
      </div>

      <v-card
        v-for="sale in recentSales"
        :key="sale.id"
        class="mb-3 pa-3"
        :class="{ 'admin-record': isAdmin && sale.reporter_email !== userEmail }"
        @click="loadSaleForEdit(sale)"
        style="cursor: pointer"
      >
        <div class="d-flex justify-space-between align-start">
          <div class="flex-grow-1">
            <div class="text-body-1 font-weight-medium mb-1">
              {{ sale.brand }} - {{ formatAmount(sale.amount) }} AED
            </div>
            <div class="d-flex align-center flex-wrap ga-2">
              <v-chip size="x-small" color="primary" variant="outlined">
                {{ sale.orders }} orders
              </v-chip>
              <v-chip size="x-small" color="blue" variant="outlined">
                {{ sale.platform }}
              </v-chip>
              <v-chip size="x-small" color="green" variant="outlined">
                {{ sale.location }}
              </v-chip>
              <span class="text-caption text-disabled">{{ sale.sales_date }}</span>
            </div>
            <v-chip
              v-if="isAdmin && sale.reporter_email !== userEmail"
              size="x-small"
              color="orange"
              class="mt-2"
            >
              {{ sale.reporter_email }}
            </v-chip>
          </div>
          <span class="text-disabled text-caption">#{{ sale.id }}</span>
        </div>
      </v-card>
    </div>
  </div>

  <v-snackbar v-model="snackBar.show" :timeout="snackBar.timeout" :color="snackBar.color">
    {{ snackBar.text }}
  </v-snackbar>

</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useSalesForm } from "@/composables/useSalesForm";
import { useApp } from "@/composables/useApp.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog";
import api from "@/utils/api.js";
import { usePlatformColors } from "@/composables/usePlatformColors.js";

const { getPlatformGradient } = usePlatformColors();

const { fetchOptions, options } = useApp();
const { confirm } = useConfirmDialog();

const snackBar = ref({
  show: false,
  text: "",
  timeout: 3000,
  color: "",
});

const {
  clearForm,
  filteredLocations,
  filteredPlatforms,
  form,
  error,
  message,
  loading,
  duplicateWarning,
  existingSaleId,
  editingId,
  recentSales,
  currentSaleReporter,
  userEmail,
  isEditMode,
  // Dialog states
  hideRecentSales,
  sourceComponent,
  closeDialog,
  completeEdit,
} = useSalesForm();

let editSaleClicked = false;

// Alert states
const showAmountAlert = ref(false);
const showOrdersAlert = ref(false);

const props = defineProps({
  isAdmin: {
    type: Boolean,
    default: false,
  },
});

onMounted(async () => {
  userEmail.value = getUserEmail();

  await fetchOptions();

  // Only fetch recent sales if not in dialog mode
  if (!hideRecentSales.value) {
    fetchRecentSales();
  }
});

// Computed properties for dropdown items
// const availableCities = computed(() => {
//   debugger
//   // Try to get cities from filterOptions first, then from salesFormOptions
//   return filterOptions.cities || salesFormOptions.cities || [];
// });

// const availableBrands = computed(() => {
//   return filterOptions.brands || salesFormOptions.brands || [];
// });

// const availablePlatforms = computed(() => {
//   return filterOptions.platforms || salesFormOptions.platforms || [];
// });

const locationHint = computed(() => {
  if (!form.city) return "City selection required";
  if (options.locations.length === 0) return `No locations found `;
  return `Available locations for ${form.city}`;
});

const platformHint = computed(() => {
  if (!form.location || !form.brand) return "Location and brand selection required";
  if (options.platforms.length === 0) return `No platforms found `;
  return `Available platforms for ${form.location} and ${form.brand}`;
});

// Check if form can be submitted (non-blocking, just for UI indication)
// const canSubmit = computed(() => {
//   // Always return true - alerts are non-blocking
//   return true;
// });

const canDeleteRecord = computed(() => {
  if (!editingId.value) return false;
  if (props.isAdmin) return true;
  return currentSaleReporter.value === userEmail.value;
});

// Watch for city changes and clear location
watch(
  () => form.city,
  (newCity, oldCity) => {
    if (newCity !== oldCity && form.location && !editSaleClicked) {
      form.location = null;
    }
    editSaleClicked = false;
  },
  { immediate: false },
);

const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDay = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-UK", { day: "numeric", month: "short", year: "numeric" });
};

// Check amount for high value alert
const checkAmount = () => {
  if (form.amount && parseFloat(form.amount) > 10000) {
    showAmountAlert.value = true;
  } else {
    showAmountAlert.value = false;
  }
};

// Check orders for high value alert
const checkOrders = () => {
  if (form.orders && parseInt(form.orders) > 100) {
    showOrdersAlert.value = true;
  } else {
    showOrdersAlert.value = false;
  }
};

/************************************
 * Recent sales
 ************************************/
const fetchRecentSales = async () => {
  try {
    const response = await api.accountingApi.get("/api/accounting/sales/analytics/recent");
    recentSales.value = response.data;
  } catch (err) {
    console.error("Failed to fetch recent sales:", err);
  }
};

const checkForDuplicate = async () => {
  if (
    !form.country ||
    !form.city ||
    !form.location ||
    !form.brand ||
    !form.platform ||
    !form.sales_date
  ) {
    return;
  }

  try {
    const existing = recentSales.value.find(
      (sale) =>
        sale.country === form.country &&
        sale.city === form.city &&
        sale.location === form.location &&
        sale.brand === form.brand &&
        sale.platform === form.platform &&
        sale.sales_date === form.sales_date,
    );

    if (existing && existing.id !== editingId.value) {
      duplicateWarning.value = true;
      existingSaleId.value = existing.id;
    } else {
      duplicateWarning.value = false;
      existingSaleId.value = null;
    }
  } catch (err) {
    console.error("Duplicate check error:", err);
  }
};

const submitSale = async () => {
  // Show confirmation dialog if amount is very high
  if (form.amount && parseFloat(form.amount) > 20000) {
    const confirmed = await confirm({
      title: "High Amount Warning",
      message: `Amount (${formatAmount(form.amount)} AED) exceeds 20,000 AED. Are you sure you want to submit?`,
      type: "warning",
      confirmText: "Submit",
    });
    if (!confirmed) return;
  }

  // Show confirmation dialog if orders are very high
  if (form.orders && parseInt(form.orders) > 200) {
    const confirmed = await confirm({
      title: "High Orders Warning",
      message: `Orders (${form.orders}) exceed 200 orders. Are you sure you want to submit?`,
      type: "warning",
      confirmText: "Submit",
    });
    if (!confirmed) return;
  }

  loading.value = true;
  duplicateWarning.value = false;
  editSaleClicked = false;

  try {
    let response;

    if (isEditMode.value) {
      response = await api.accountingApi.put(`/api/accounting/sales/${editingId.value}`, form);
      snackBar.value.show = true;
      snackBar.value.color = "success";
      snackBar.value.text = `Sale ${editingId.value} updated successfully!`;

      // If editing from dialog, close it
      if (sourceComponent.value === "rawdata") {
        completeEdit("update");
      }
    } else {
      response = await api.accountingApi.post("/api/accounting/sales", form);
      snackBar.value.show = true;
      snackBar.value.color = "success";
      snackBar.value.text = "Sale recorded successfully!";
    }

    // Reset form
    resetForm();
    editingId.value = null;
    currentSaleReporter.value = "";

    // Clear alerts
    showAmountAlert.value = false;
    showOrdersAlert.value = false;

    // Refresh recent sales only if not in dialog mode
    if (sourceComponent.value !== "rawdata") {
      fetchRecentSales();
    }
  } catch (err) {
    snackBar.value.show = true;
    snackBar.value.color = "error";
    if (err.response?.status === 409) {
      duplicateWarning.value = true;
      existingSaleId.value = err.response.data.existingId;
      snackBar.value.text = err.response?.data?.error;
    } else {
      snackBar.value.text = err.response?.data?.error || "Failed to submit sale";
    }
  } finally {
    loading.value = false;
  }
};

const loadExistingRecord = async () => {
  if (!existingSaleId.value) return;

  try {
    const response = await api.accountingApi.get(`/api/accounting/sales/record/${existingSaleId.value}`);
    const sale = response.data;

    // Populate form with existing data
    form.country = sale.country;
    form.city = sale.city;
    form.location = sale.location;
    form.brand = sale.brand;
    form.platform = sale.platform;
    form.amount = sale.amount;
    form.orders = sale.orders;
    // Convert ISO date to yyyy-MM-dd format for date input
    form.sales_date = sale.sales_date ? sale.sales_date.split("T")[0] : sale.sales_date;

    editingId.value = sale.id;
    currentSaleReporter.value = sale.reporter_email;
    duplicateWarning.value = false;
  } catch (err) {
    snackBar.value.show = true;
    snackBar.value.color = "error";
    snackBar.value.text = "Failed to load existing record";
  }
};

const loadSaleForEdit = async (sale) => {
  editSaleClicked = true;
  try {
    // For admin users, we need to fetch the full sale record to ensure permissions
    const response = await api.accountingApi.get(`/api/accounting/sales/record/${sale.id}`);
    const fullSale = response.data;

    form.country = fullSale.country;
    form.city = fullSale.city;
    form.location = fullSale.location;
    form.brand = fullSale.brand;
    form.platform = fullSale.platform;
    form.amount = fullSale.amount;
    form.orders = fullSale.orders;
    // Convert ISO date to yyyy-MM-dd format for date input
    form.sales_date = fullSale.sales_date ? fullSale.sales_date.split("T")[0] : fullSale.sales_date;

    editingId.value = fullSale.id;
    currentSaleReporter.value = fullSale.reporter_email;
    duplicateWarning.value = false;
    error.value = "";
    message.value = "";

    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (err) {
    snackBar.value.show = true;
    snackBar.value.color = "error";
    snackBar.value.text = "Failed to load sale record for editing";
  }
};

const handleCancel = () => {
  if (sourceComponent.value === "rawdata") {
    closeDialog();
  } else {
    editingId.value = null;
    currentSaleReporter.value = "";
    resetForm();
  }
};

const deleteRecord = async () => {
  if (!editingId.value) return;

  const confirmed = await confirm({
    title: "Delete Sale Record",
    message: "Are you sure you want to delete this sale record?",
    type: "danger",
    confirmText: "Delete",
  });
  if (!confirmed) return;

  try {
    await api.accountingApi.delete(`/api/accounting/sales/${editingId.value}`);
    snackBar.value.show = true;
    snackBar.value.color = "success";
    snackBar.value.text = "Sale record deleted successfully!";

    // If deleting from dialog, close it
    if (sourceComponent.value === "rawdata") {
      completeEdit("delete");
    } else {
      editingId.value = null;
      currentSaleReporter.value = "";
      resetForm();
      fetchRecentSales();
    }
  } catch (err) {
    snackBar.value.show = true;
    snackBar.value.color = "error";
    snackBar.value.text = err.response?.data?.error || "Failed to delete record";
  }
};

const resetForm = () => {
  form.amount = null;
  form.orders = null;
  form.platform = null;
  // form.platform = "";
  // form.sales_date = new Date().toISOString().split("T")[0];
  duplicateWarning.value = false;
  existingSaleId.value = null;
  editSaleClicked = false;
};

// Get user email from token
const getUserEmail = () => {
  const token = localStorage.getItem("authToken");
  return token || "";
};

// Watch for form changes to check duplicates
watch(
  [
    () => form.country,
    () => form.city,
    () => form.location,
    () => form.brand,
    () => form.platform,
    () => form.sales_date,
  ],
  () => {
    if (error.value) {
      error.value = "";
    }
    if (!isEditMode.value) {
      checkForDuplicate();
    }
  },
);
</script>

<style scoped>
.admin-record {
  border-left: 4px solid orange !important;
}
</style>
